<template>
  <div
    class="grid grid-cols-1 lg:grid-cols-2 gap-5 items-center md:h-screen mt-5 md:mt-0"
  >
    <section class="w-full sm:w-auto lg:w-full">
      <div class="bg-white shadow-md rounded-xl overflow-hidden lg:m-10">
        <div class="bg-gray-50 p-5 border-b">
          <h4>
            {{ $t("auth.login.title") }}
          </h4>
        </div>
        <form @submit.prevent="loginUser()">
          <div class="px-5 py-5">
            <div class="flex flex-col my-4">
              <InputText
                id="username-input"
                type="text"
                v-model="form.username"
                :placeholder="$t('auth.login.email')"
                :class="v$.form.username.$error ? 'p-invalid' : ''"
              />
              <Errors :errors="v$.form.username.$errors" />
            </div>
            <div class="flex flex-col my-4">
              <InputPassword
                id="password-input"
                v-model="form.password"
                :feedback="false"
                toggleMask
                :placeholder="$t('auth.login.password')"
                :class="v$.form.password.$error ? 'p-invalid' : ''"
              />
              <Errors :errors="v$.form.password.$errors" />
            </div>
            <div
              class="flex justify-between flex-col md:flex-row text-center gap-5"
            >
              <div
                class="field-checkbox flex gap-2 items-center "
              >
                <Checkbox
                  inputId="binary"
                  v-model="isRemember"
                  :binary="true"
                />
                <label for="binary">
                  {{ $t("auth.login.keep") }}
                </label>
              </div>
                <a
                  id="forgot-password-link"
                  @click="go.redirectTo('forgot-password')"
                  class="flex md:text-sm font-[700] text-h-primary-500 hover:text-h-primary-700 cursor-pointer"
                >
                  {{ $t("auth.login.forgot") }}
                </a>
            </div>
            <div class="mt-5">
              {{ $t("auth.login.register") }}
              <RouterLink to="/signup" class="text-h-primary-500">
                {{ $t("auth.login.link") }}
              </RouterLink>
            </div>
          </div>

          <div
            class="bg-gray-50 p-4 flex justify-end items-center mt-3 border-t border-t-gray-300 rounded-b-lg"
          >
            <Button
              type="submit"
              :label="$t('labels.enter')"
              class="btn-primary"
            />
          </div>
        </form>
      </div>
    </section>

    <section class="hidden lg:flex justify-end">
      <ComputerComponent />
    </section>
  </div>
</template>

<script lang="ts">
import { email, minLength, required } from "@/locale/locale.config";
import { Authentication } from "@/models/auth.model";
import { Roles } from "@/models/user.model";
import { AuthRest } from "@/services/rest/auth.rest";
import { GoToRoute } from "@/utils/go-to.util";
import HandleError from "@/utils/handle-errors.util";
import { setItemLocalStorage } from "@/utils/save-storage.util";
import { storeUser } from "@/utils/user-store.util";
import useVuelidate from "@vuelidate/core";
import { defineAsyncComponent, defineComponent } from "vue";

const ComputerComponent = defineAsyncComponent(
  () => import("./computer-component.vue")
);

const authRest = new AuthRest();
export default defineComponent({
  name: "login",
  components: { ComputerComponent },
  computed: {
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
  watch: {
    isRemember() {
      setItemLocalStorage("isRemember", this.isRemember ? "true" : "false");
    },
    "form.username": {
      handler(val) {
        this.form.username = val?.trim();
      },
      immediate: true,
    },
    "form.password": {
      handler(val) {
        this.form.password = val?.trim();
      },
      immediate: true,
    },
  },
  data() {
    return {
      form: new Authentication(),
      isRemember: true as boolean,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  mounted() {
    setItemLocalStorage("isRemember", this.isRemember ? "true" : "false");
  },
  validations() {
    return {
      form: {
        username: { email, required },
        password: { required, min: minLength(3) },
      },
    };
  },
  methods: {
    loginUser(): void {
      this.v$.form.$touch();
      if (this.v$.form.$error) return;

      authRest
        .loginUser({
          username: this.form.username,
          password: this.form.password,
        })
        .subscribe({
          next: (response) => {
            if (response.role == Roles.ADMINISTRATOR) {
              this.$router.push({ name: "dashboardAdm" });
            } else if (response.role == Roles.STUDENT) {
              this.$router.push({ name: "dashboardStd" });
            } else if (response.role == Roles.TEACHER) {
              this.$router.push({ name: "dashboardTeacher" });
            } else {
              this.$router.push({ name: "organizations" });
            }
            storeUser(response);
          },
          error() {
            new HandleError().renderToastError("ERR_BAD_REQUEST");
          },
        });
    },
  },
});
</script>
<style lang="scss">
#custom-login-svg {
  svg,
  path {
    width: 100%;
    height: 300px;
    @apply fill-current #{!important};
  }
}
</style>
